import React from 'react'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import DeleteIcon from '@mui/icons-material/Delete'

import { SelectFilterScope, SelectParcelFilterScope } from '../../tcm_scopes'
import { FilterSelectOption, SelectOperator } from '../../tcm_service'

import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'

import { Divider } from '@mui/material'
import {
    FilterAccordion,
    FilterAccordionDetails,
    FilterAccordionSummary
} from 'src/components/filteraccordion/filter_accordion'
import { JunctionMode } from '../../tcm_scopes'
import { FieldIconButton, FieldRow, FilterPanel, criterionLabel } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('FilterSelectView')

// :: FilterSelectView

type SelectFilterViewProps = {
    className?: string
    scope: SelectFilterScope
}

class SelectFilterViewClass implements FCClassContext<SelectFilterViewProps> {
    // :: Fields
    scope!: SelectFilterScope
    setAnchorEl!: React.Dispatch<React.SetStateAction<HTMLElement | null>>

    onSyncState({ scope }: SelectFilterViewProps) {
        this.scope = scope
    }

    render({ className }: SelectFilterViewProps) {
        const { scope } = this

        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
        this.setAnchorEl = setAnchorEl

        const selectOptions = scope.getOptions()
        const menuAndOrOpened = !!anchorEl

        return (
            <FilterAccordion className={className} expanded={scope.expanded} onChange={this.handleExpandedChange}>
                <FilterAccordionSummary
                    onClear={this.handleClearAllClicked}
                    onAdd={this.handlePlusClick}
                    caption={scope.caption}
                    captionBold={!!scope.conjunction.length || !!scope.disjunction.length}
                />
                <FilterAccordionDetails>
                    {!!scope.conjunction.length &&
                        scope.conjunction.map((parcelScope, idx, arr) => (
                            <SelectParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                selectOptions={selectOptions}
                                scope={parcelScope}
                                divider={idx < arr.length - 1 || !!scope.disjunction.length}
                            />
                        ))}

                    {!!scope.disjunction.length &&
                        scope.disjunction.map((parcelScope, idx, arr) => (
                            <SelectParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                selectOptions={selectOptions}
                                scope={parcelScope}
                                divider={idx < arr.length - 1}
                            />
                        ))}

                    {!scope.conjunction.length && !scope.disjunction.length && (
                        <FieldHelp>Nenhum critério adicionado. Use o + para incluir um.</FieldHelp>
                    )}
                </FilterAccordionDetails>
            </FilterAccordion>
        )
    } //

    readonly handleExpandedChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        this.scope.onExpandedChange(expanded).catch(LOG.caught)
    }

    readonly handlePlusClick = () => {
        //this.setAnchorEl(event.currentTarget)
        this.handleAddORClicked()
    }

    readonly handleClose = () => {
        this.setAnchorEl(null)
    }

    readonly handleAddANDClicked = () => {
        this.setAnchorEl(null)
        this.scope.onAddFilter(JunctionMode.AND).catch(LOG.caught)
    }

    readonly handleAddORClicked = () => {
        this.setAnchorEl(null)
        this.scope.onAddFilter(JunctionMode.OR).catch(LOG.caught)
    }

    readonly handleClearAllClicked = () => {
        this.setAnchorEl(null)
        this.scope.onClearAllFilters().catch(LOG.caught)
    }
}

export const SelectFilterView = classToFComponent(SelectFilterViewClass, React)
export default SelectFilterView

// :: SelectParcelViewClass

type SelectParcelViewProps = {
    className?: string
    acceptBlankValues: boolean
    selectOptions: FilterSelectOption[]
    scope: SelectParcelFilterScope
    divider?: boolean
}

class SelectParcelViewClass implements FCClassContext<SelectParcelViewProps> {
    scope!: SelectParcelFilterScope

    render({ className, acceptBlankValues, selectOptions, scope, divider }: SelectParcelViewProps) {
        this.scope = scope

        return (
            <>
                <FilterPanel className={className}>
                    <Field>
                        <FieldLabel htmlFor={scope.uid}>
                            {criterionLabel(scope.junctionMode === JunctionMode.AND)}
                        </FieldLabel>
                        <TextField
                            id={scope.uid}
                            select
                            size="small"
                            defaultValue={`${SelectOperator.is}`}
                            value={`${scope.operator}`}
                            onChange={this.handleSelectChange}
                            sx={inputSx}
                            fullWidth
                        >
                            <MenuItem value={SelectOperator.is}>É igual</MenuItem>
                            <MenuItem value={SelectOperator.is_not}>Não é igual</MenuItem>
                            {acceptBlankValues && <MenuItem value={SelectOperator.is_blank}>Está em branco</MenuItem>}
                            {acceptBlankValues && (
                                <MenuItem value={SelectOperator.is_not_blank}>Não está em branco</MenuItem>
                            )}
                        </TextField>
                    </Field>
                    {scope.valueVisible && (
                        <FieldRow>
                            <Field style={{ flexGrow: 1 }}>
                                <FieldLabel htmlFor={`${scope.uid}_filtro`}>Valor</FieldLabel>
                                <TextField
                                    id={`${scope.uid}_filtro`}
                                    select
                                    size="small"
                                    value={`${scope.value}`}
                                    onChange={this.handleFromValueChange}
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        select: { displayEmpty: true }
                                    }}
                                >
                                    {selectOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.title}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Field>
                            <FieldIconButton size="small" aria-label="Limpar valor" onClick={this.handleClearClick}>
                                <DeleteIcon />
                            </FieldIconButton>
                        </FieldRow>
                    )}
                </FilterPanel>
                {divider && <Divider sx={{ borderColor: 'var(--border)' }} />}
            </>
        )
    }

    readonly handleSelectChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onOperatorChange(+event.target.value)
    }

    readonly handleFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onValueChange(event.target.value, 'from').catch(LOG.caught)
    }

    readonly handleClearClick = () => {
        this.scope.onClear().catch(LOG.caught)
    }

    readonly handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            this.scope.onEnterKeyPressed().catch(LOG.caught)
        }
    }
}

export const SelectParcelView = classToFComponent(SelectParcelViewClass, React)
